import React, { useState } from 'react';
import { Modal } from 'components/Modal';
import { Text } from 'components/Text';

// Helper function for className concatenation
const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(' ');
};

interface ConfirmationProps {
  isOpen?: boolean;
  title?: string;
  message?: string;
  subMessage?: string;
  onConfirm?: () => void;
  btnText?: string;
}

const defaultConfirmation = {
  isOpen: false,
  title: '',
  message: '',
  subMessage: '',
  onConfirm: () => {},
  btnText: 'Confirm',
};

function withConfirmation(WrappedComponent: any) {
  const WithConfirmation = (props: any) => {
    const [confirmation, setConfirmation] = useState<ConfirmationProps>({
      ...defaultConfirmation,
    });

    const handleConfirm = () => {
      if (confirmation.onConfirm) {
        confirmation.onConfirm();
      }
      setConfirmation(defaultConfirmation);
    };

    const handleCancel = () => {
      setConfirmation(defaultConfirmation);
    };

    const setShowConfirmation = (props: ConfirmationProps) => {
      setConfirmation({
        ...props,
        isOpen: true,
      });
    };

    return (
      <>
        {confirmation.isOpen && (
          <Modal
            open={confirmation.isOpen}
            openModal={() => setConfirmation(defaultConfirmation)}
            containerClass="sm:max-w-lg w-full sm:w-auto p-5"
          >
            <div className="sm:flex sm:items-start mt-3">
              <div className="text-center sm:mt-0 sm:ml-4 sm:text-left">
                <div className="mt-2">
                  {confirmation.title && (
                    <Text
                      className="text-sm text-gray-500"
                      type="h4"
                      value={confirmation.title}
                    />
                  )}
                  {confirmation.message && (
                    <p className="text-sm text-gray-500">
                      {confirmation.message}
                    </p>
                  )}
                  {confirmation.subMessage && (
                    <p className="text-sm text-gray-500">
                      {confirmation.subMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className={classNames(
                  'w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm',
                )}
                onClick={handleConfirm}
              >
                {confirmation.btnText}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </Modal>
        )}
        <WrappedComponent
          {...props}
          setShowConfirmation={setShowConfirmation}
        />
      </>
    );
  };

  return WithConfirmation;
}

export default withConfirmation;
